package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.example.project.CreateLicenseKeyResult
import org.example.project.FirebaseManager
import org.example.project.PrimaryColor

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateLicenseKeysSection(
    suggestedLicenseUnits: List<Int>,
    userPoints: Int,
    onCodesCreated: () -> Unit
) {
    val scope = rememberCoroutineScope()
    
    val availableOptions = remember(suggestedLicenseUnits, userPoints) {
        suggestedLicenseUnits.filter { it <= userPoints }
    }
    
    var createLicenseUnits by remember { mutableStateOf("") }
    
    // Auto-update only if the current value is invalid/too small.
    LaunchedEffect(availableOptions, userPoints) {
        if (availableOptions.isNotEmpty()) {
            val currentVal = createLicenseUnits.toIntOrNull() ?: 0
            if (!availableOptions.contains(currentVal)) {
                 createLicenseUnits = availableOptions.maxOrNull()?.toString() ?: ""
            }
        } else if (userPoints > 0) {
            // If no suggested options fit but the user has some points, default to all their points.
            createLicenseUnits = userPoints.toString()
        }
    }
    
    var createCount by remember { mutableStateOf("1") }
    var isCreatingCodes by remember { mutableStateOf(false) }
    var createCodesMessage by remember { mutableStateOf<String?>(null) }
    var expanded by remember { mutableStateOf(false) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Generate License Keys",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))
            
            ExposedDropdownMenuBox(
                expanded = expanded,
                onExpandedChange = { expanded = !expanded }
            ) {
                OutlinedTextField(
                    value = createLicenseUnits,
                    onValueChange = { createLicenseUnits = it.filter { char -> char.isDigit() } },
                    label = { Text("Licenses per key") },
                    placeholder = { Text("Enter volume") },
                    modifier = Modifier.fillMaxWidth().menuAnchor(),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PrimaryColor,
                        unfocusedBorderColor = Color(0xFFE2E8F0)
                    ),
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) }
                )
                
                if (availableOptions.isNotEmpty()) {
                    MaterialTheme(
                        colorScheme = MaterialTheme.colorScheme.copy(surface = Color.White, onSurface = Color(0xFF334155)),
                        shapes = MaterialTheme.shapes.copy(extraSmall = MaterialTheme.shapes.small)
                    ) {
                        ExposedDropdownMenu(
                            expanded = expanded,
                            onDismissRequest = { expanded = false },
                            modifier = Modifier.fillMaxWidth(0.9f)
                        ) {
                            availableOptions.forEach { license_unitsOption ->
                                val isSelected = createLicenseUnits == license_unitsOption.toString()
                                DropdownMenuItem(
                                    text = { 
                                        Text(
                                            text = "$license_unitsOption Licenses",
                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                            color = if (isSelected) PrimaryColor else Color(0xFF475569) // Slate 600
                                        ) 
                                    },
                                    onClick = {
                                        createLicenseUnits = license_unitsOption.toString()
                                        expanded = false
                                    },
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(12.dp))
            
            OutlinedTextField(
                value = createCount,
                onValueChange = { createCount = it.filter { char -> char.isDigit() } },
                label = { Text("Batch size") },
                placeholder = { Text("Number of keys") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            
            Spacer(modifier = Modifier.height(16.dp))
            
            Button(
                onClick = {
                    val license_units = createLicenseUnits.toIntOrNull() ?: 0
                    val count = createCount.toIntOrNull() ?: 0
                    if (license_units > 0 && count > 0) {
                        scope.launch {
                            isCreatingCodes = true
                            createCodesMessage = null
                            val result = FirebaseManager.createLicenseKeys(
                                license_units,
                                count
                            )
                            when (result) {
                                is CreateLicenseKeyResult.Success -> {
                                    createCodesMessage = "Success: Generated $count license keys!"
                                    createCount = "1"
                                    onCodesCreated()
                                }
                                is CreateLicenseKeyResult.Error -> {
                                    createCodesMessage = "Error: ${result.message}"
                                }
                            }
                            isCreatingCodes = false
                        }                                
                    }
                },
                enabled = !isCreatingCodes && (createLicenseUnits.toIntOrNull() ?: 0) in 1..userPoints && (createCount.toIntOrNull() ?: 0) > 0,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                shape = MaterialTheme.shapes.medium
            ) {
                if (isCreatingCodes) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("Generate", fontWeight = FontWeight.Bold)
                }
            }

            if (createCodesMessage != null) {
                val isError = createCodesMessage!!.startsWith("Error")
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = if (isError) Color(0xFFFEF2F2) else Color(0xFFF0FDF4),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = createCodesMessage!!,
                            color = if (isError) Color(0xFFDC2626) else Color(0xFF16A34A),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }
}
